import Phaser from 'phaser';
import { OutfitRenderer, type EquippedMap } from './OutfitRenderer';
import { svgStringToTexture } from '../utils/svgToTexture';

/**
 * CharacterAnimator — tween-based character animations + real 3-frame walk cycle.
 *
 * Walk cycle usage:
 *   const anim = await CharacterAnimator.startWalk(scene, sprite, equipped, baseY);
 *   anim.stop(); // stops the walk cycle
 */
export const CharacterAnimator = {
  /** Gentle breathing bob — the "resting" animation */
  idle(scene: Phaser.Scene, sprite: Phaser.GameObjects.Image): Phaser.Tweens.Tween {
    return scene.tweens.add({
      targets: sprite,
      y: sprite.y - 4,
      duration: 1500,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.InOut',
    });
  },

  /**
   * Real walk cycle: pre-renders 3 frames (idle/step-left/step-right) and
   * swaps textures every 200ms while tweening the sprite body.
   * Returns a controller object with a stop() method.
   */
  async startWalk(
    scene: Phaser.Scene,
    sprite: Phaser.GameObjects.Image,
    equipped: EquippedMap,
  ): Promise<{ stop: () => void }> {
    const baseY = sprite.y;
    const baseX = sprite.x;
    const scale = sprite.scale;

    // Pre-render 3 frames
    const frameKeys: string[] = [];
    const frames: (0 | 1 | 2)[] = [0, 1, 0, 2]; // idle, left, idle, right
    for (const f of [0, 1, 2] as const) {
      const key = `walk_frame_${f}_${Date.now()}`;
      const svg = OutfitRenderer.renderCharacterSVG(equipped, f);
      try {
        await svgStringToTexture(scene, key, svg);
        frameKeys.push(key);
      } catch {
        frameKeys.push(''); // fallback to same texture
      }
    }

    let frameIdx = 0;
    let stopped = false;

    // Body bob tween
    const bobTween = scene.tweens.add({
      targets: sprite,
      y: baseY - 8,
      duration: 250,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.InOut',
    });
    // Slight sway
    const swayTween = scene.tweens.add({
      targets: sprite,
      x: baseX + 3,
      angle: 1.5,
      duration: 250,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.InOut',
    });

    // Texture swap loop (every 200ms)
    const swapFrame = (): void => {
      if (stopped || !sprite.active) return;
      const f = frames[frameIdx % frames.length];
      const key = frameKeys[f];
      if (key && scene.textures.exists(key)) {
        sprite.setTexture(key);
        sprite.setScale(scale);
      }
      frameIdx++;
      scene.time.delayedCall(200, swapFrame);
    };
    swapFrame();

    return {
      stop: () => {
        stopped = true;
        bobTween.stop();
        swayTween.stop();
        sprite.setY(baseY);
        sprite.setX(baseX);
        sprite.setAngle(0);
        // Restore frame 0
        if (frameKeys[0] && scene.textures.exists(frameKeys[0])) {
          sprite.setTexture(frameKeys[0]);
          sprite.setScale(scale);
        }
      },
    };
  },

  /** Walking — tween-based body movement (use startWalk for real frame cycle) */
  walk(scene: Phaser.Scene, sprite: Phaser.GameObjects.Image): Phaser.Tweens.Tween {
    const baseY = sprite.y;
    const baseX = sprite.x;
    // Vertical bob
    scene.tweens.add({
      targets: sprite,
      y: baseY - 8,
      duration: 300,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.InOut',
    });
    // Side sway (rotation)
    return scene.tweens.add({
      targets: sprite,
      x: baseX + 3,
      angle: 2,
      duration: 300,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.InOut',
    });
  },

  /** Happy wave — scale up with a rotation tilt then back, repeat a few times */
  wave(scene: Phaser.Scene, sprite: Phaser.GameObjects.Image): Phaser.Tweens.Tween {
    const baseScale = sprite.scale;
    return scene.tweens.add({
      targets: sprite,
      scaleX: baseScale * 1.12,
      scaleY: baseScale * 1.12,
      angle: 8,
      duration: 180,
      yoyo: true,
      repeat: 4,
      ease: 'Sine.InOut',
      onComplete: () => {
        sprite.setScale(baseScale);
        sprite.setAngle(0);
      },
    });
  },

  /** Dance — repeated scale pulse + rotation used in runway / win screens */
  dance(scene: Phaser.Scene, sprite: Phaser.GameObjects.Image): Phaser.Tweens.Tween {
    const baseScale = sprite.scale;
    scene.tweens.add({
      targets: sprite,
      y: sprite.y - 12,
      duration: 250,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.InOut',
    });
    return scene.tweens.add({
      targets: sprite,
      scaleX: baseScale * 1.08,
      scaleY: baseScale * 1.08,
      angle: 6,
      duration: 250,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.InOut',
    });
  },

  /** Cheer — small jump (y up 20px) + scale pulse */
  cheer(scene: Phaser.Scene, sprite: Phaser.GameObjects.Image): Phaser.Tweens.Tween {
    const baseScale = sprite.scale;
    const baseY = sprite.y;
    scene.tweens.add({
      targets: sprite,
      y: baseY - 20,
      duration: 200,
      yoyo: true,
      repeat: 3,
      ease: 'Sine.Out',
    });
    return scene.tweens.add({
      targets: sprite,
      scaleX: baseScale * 1.15,
      scaleY: baseScale * 1.15,
      duration: 200,
      yoyo: true,
      repeat: 3,
      ease: 'Sine.InOut',
      onComplete: () => {
        sprite.setScale(baseScale);
        sprite.setY(baseY);
      },
    });
  },

  /** Sad — tilt down with gray tint */
  sad(scene: Phaser.Scene, sprite: Phaser.GameObjects.Image): Phaser.Tweens.Tween {
    sprite.setTint(0xaaaaaa);
    return scene.tweens.add({
      targets: sprite,
      angle: 15,
      y: sprite.y + 10,
      duration: 400,
      ease: 'Sine.Out',
    });
  },

  /** Reset to neutral — clear all tweens on sprite, restore angle/tint/scale */
  reset(scene: Phaser.Scene, sprite: Phaser.GameObjects.GameObject): void {
    scene.tweens.killTweensOf(sprite);
    const s = sprite as unknown as { setAngle?: (a: number) => void; clearTint?: () => void };
    if (typeof s.setAngle === 'function') s.setAngle(0);
    if (typeof s.clearTint === 'function') s.clearTint();
  },
};
